<script setup>
import { useAccount } from 'dashboard/composables/useAccount';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FeatureSpotlight from 'dashboard/components-next/feature-spotlight/FeatureSpotlight.vue';
import { documentsListInLib } from 'dashboard/components-next/pilot/pageComponents/emptyStates/pilotEmptyStateContent.js';

// 导入 SVG 图片
import documentLight from 'assets/images/dashboard/pilot/document-light.svg';
import documentDark from 'assets/images/dashboard/pilot/document-dark.svg';

const emit = defineEmits(['click']);
const { isOnChatwootCloud } = useAccount();

const onClick = () => {
  emit('click');
};

// 格式化文件大小
const formatFileSize = bytes => {
  if (!bytes) return '';
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  const size = parseFloat((bytes / k ** i).toFixed(1));
  return `${size} ${sizes[i]}`;
};

// 获取创建人姓名首字母
const getInitial = name => {
  return name?.charAt(0)?.toUpperCase() || '?';
};

// 获取创建人头像颜色
const getCreatorColor = name => {
  const colors = [
    '#8B5CF6',
    '#3B82F6',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#EC4899',
    '#6366F1',
  ];
  const index = name?.charCodeAt(0) || 0;
  return colors[index % colors.length];
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <FeatureSpotlight
      :title="
        $t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.EMPTY_FEATURE_SPOTLIGHT_TITLE')
      "
      :note="$t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.EMPTY_FEATURE_SPOTLIGHT_NOTE')"
      :fallback-thumbnail="documentLight"
      :fallback-thumbnail-dark="documentDark"
      learn-more-url=""
      :hide-actions="!isOnChatwootCloud"
    />
    <EmptyStateLayout
      :title="$t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.EMPTY_TITLE')"
      :subtitle="$t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.EMPTY_SUBTITLE')"
      :action-perms="['administrator']"
    >
      <template #empty-state-item>
        <div class="w-full space-y-3">
          <!-- 假数据文档列表 -->
          <div
            v-for="(doc, index) in documentsListInLib.slice(0, 5)"
            :key="`doc-${index}`"
            class="flex items-center justify-between p-4 bg-n-slate-white dark:bg-n-slate-800 rounded-lg border border-n-weak"
          >
            <!-- 左侧：图标 + 文档信息 -->
            <div class="flex items-center gap-3 flex-1 min-w-0">
              <!-- 图标 -->
              <div class="h-5 w-5 text-n-slate-500 flex-shrink-0">
                <div
                  :class="
                    doc.content_type === 'application/pdf'
                      ? 'i-lucide-file-text'
                      : 'i-lucide-link'
                  "
                  class="h-5 w-5"
                />
              </div>

              <!-- 文档信息 -->
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-1">
                  <h4
                    class="text-sm font-medium text-n-slate-900 dark:text-n-slate-100 truncate"
                  >
                    {{ doc.name }}
                  </h4>
                  <!-- 状态标签 -->
                  <span
                    class="px-2 py-0.5 text-xs font-medium rounded-full shrink-0 bg-n-green-1 dark:bg-n-green-2 text-n-green-10 dark:text-n-green-9"
                  >
                    已解析
                  </span>
                </div>

                <!-- 文档内容预览 -->
                <div
                  v-if="doc.content"
                  class="text-xs text-n-slate-600 dark:text-n-slate-400 line-clamp-2"
                >
                  {{ doc.content }}
                </div>

                <!-- 外部链接 -->
                <div
                  v-else-if="doc.external_link"
                  class="text-xs text-n-blue-600 dark:text-n-blue-400 truncate"
                >
                  {{ doc.external_link }}
                </div>

                <!-- 创建人信息 -->
                <div v-if="doc.creator" class="flex items-center gap-2 mt-2">
                  <div
                    class="w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium text-white"
                    :style="{
                      backgroundColor: getCreatorColor(doc.creator.name),
                    }"
                  >
                    {{ getInitial(doc.creator.name) }}
                  </div>
                  <span class="text-xs text-n-slate-600 dark:text-n-slate-400">
                    {{ doc.creator.name }}
                  </span>
                </div>
              </div>
            </div>

            <!-- 右侧：大小 + 时间 -->
            <div class="flex items-center gap-3">
              <!-- 文件大小 -->
              <span
                v-if="doc.metadata?.file_size"
                class="text-xs text-n-slate-500 dark:text-n-slate-500 shrink-0"
              >
                {{ formatFileSize(doc.metadata.file_size) }}
              </span>

              <!-- 时间 -->
              <span
                class="text-xs text-n-slate-500 dark:text-n-slate-500 shrink-0"
              >
                {{ doc.time_ago }}
              </span>
            </div>
          </div>
        </div>
      </template>
      <template #actions>
        <Button
          :label="$t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.UPLOAD')"
          icon="i-lucide-upload-cloud"
          @click="onClick"
        />
      </template>
    </EmptyStateLayout>
  </div>
</template>
